import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, Cross1Icon } from '@radix-ui/react-icons';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { TUserWithAccessControl } from '@/features/users/schemas/users.types.ts';
import {
  getCompactUserRole,
  getLabelRole,
  getLabelStatus,
  isUserEligibleForAssignment,
} from '@/features/users/schemas/users.utils.ts';

const USERS_PER_PAGE = 5;

export const RuleAssignmentManagementMobile = ({
  users,
  onMoveUser,
  isLoading,
}: {
  users: TUserWithAccessControl[];
  onMoveUser: (userId: string, targetColumnId: string) => void;
  isLoading: boolean;
}) => {
  const availableUsers = users
    ?.filter((u) => u.columnId === 'available-users')
    ?.map((u) => ({
      value: u.userId,
      label: `${u.firstName} ${u.lastName}`,
      role: u.role,
      eligible: isUserEligibleForAssignment(u),
    }));
  const hasEligibleUsers = availableUsers?.some((u) => u.eligible);
  const associatedUsers = users
    .filter((u) => u.columnId === 'associated-users')
    .sort((a, b) => {
      if (a.newlyAdded && !b.newlyAdded) return 1;
      if (!a.newlyAdded && b.newlyAdded) return -1;
      return 0;
    });

  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const numOfPages = associatedUsers?.length
    ? Math.trunc((associatedUsers.length - 1) / USERS_PER_PAGE) + 1
    : 1;
  const activePage = Math.min(currentPage, numOfPages);
  const indexOfLastUser = activePage * USERS_PER_PAGE;
  const indexOfFirstUser = indexOfLastUser - USERS_PER_PAGE;
  const currentUsers = associatedUsers.slice(indexOfFirstUser, indexOfLastUser);

  if (isLoading)
    return (
      <div className="flex min-h-[332px] w-full flex-col">
        <Skeleton className="h-9" />

        <div className="mt-4 flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
        <div className="mt-4 flex h-full items-center justify-between">
          <Skeleton className="h-5 w-16" />

          <div className="flex gap-2">
            <Skeleton className="size-8" />
            <Skeleton className="size-8" />
          </div>
        </div>
      </div>
    );

  return (
    <div className="mt-4 flex min-h-[332px] flex-col justify-between">
      <div>
        <Popover open={comboboxOpen} onOpenChange={setComboboxOpen} modal>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={comboboxOpen}
              className="h-9 w-full justify-between font-normal"
              disabled={!hasEligibleUsers}
            >
              {hasEligibleUsers ? 'Assign a user' : 'All available users are assigned'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search users..." />
              <CommandList>
                <CommandEmpty>No users found.</CommandEmpty>
                <CommandGroup>
                  {availableUsers
                    ?.filter((u) => u.eligible)
                    ?.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={`${option.label} ${option.role}`}
                        onSelect={() => {
                          onMoveUser(option.value, 'associated-users');
                          setComboboxOpen(false);
                          const newNumOfPages =
                            Math.trunc(associatedUsers.length / USERS_PER_PAGE) + 1;
                          setCurrentPage(newNumOfPages);
                        }}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{option.label}</span>
                        <Badge variant={getLabelRole(option.role)} className="shrink-0">
                          {option.role}
                        </Badge>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div>
          {associatedUsers.length === 0 ? (
            <p className="text-muted-foreground mt-6 text-sm">No users assigned</p>
          ) : (
            <ul className="mt-4 flex flex-col gap-2">
              {currentUsers.map((user) => {
                return (
                  <li
                    key={user.userId}
                    className="flex items-center gap-2 overflow-hidden rounded-lg bg-slate-100 px-4 py-2 aria-disabled:opacity-50 aria-disabled:hover:ring-0"
                  >
                    <div className="flex min-w-0 flex-1 items-center">
                      <span className="truncate">
                        {user.firstName} {user.lastName}
                      </span>
                      {user.newlyAdded && (
                        <span className="mt-1 ml-1 size-2 flex-none rounded-full bg-blue-400"></span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {user.status !== 'Active' && (
                        <Badge variant={getLabelStatus(user.status)} className="shrink-0">
                          {user.status}
                        </Badge>
                      )}
                      <Badge variant={getLabelRole(user.role)} className="shrink-0">
                        {getCompactUserRole(user.role, true)}
                      </Badge>
                      <button
                        onClick={() => {
                          onMoveUser(user.userId, 'available-users');

                          if (currentUsers.length % 5 === 1) {
                            setCurrentPage((currentPage) =>
                              currentPage === 1 ? currentPage : currentPage - 1
                            );
                          }
                        }}
                        className="hover:ring-primary p-1 hover:ring-2 hover:ring-inset"
                      >
                        <Cross1Icon />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
            Page {activePage} of {numOfPages}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (activePage <= 1) return;
                setCurrentPage((current) => current - 1);
              }}
              disabled={activePage <= 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (activePage >= numOfPages) return;
                setCurrentPage((current) => current + 1);
              }}
              disabled={activePage >= numOfPages}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
